import React, { Suspense, useEffect, useState } from 'react';
import MDX from '@mdx-js/runtime';
import ErrorBoundry from '../common/ErrorBoundry/ErrorBoundry';
import { useSelector, shallowEqual } from 'react-redux';
import yaml from 'js-yaml';
import Asset from '../Asset/Asset';
import useAxios from 'axios-hooks';
import CmsTools from '../../CmsTools/CmsTools';
import Loader from '../common/Loading/Loading';
// no longer lazy loading these components to reduce breakages
import Mbar from '../Media/Mbar/Mbar';
import CommandWindow from '../Media/CommandWindow/CommandWindow';
import Svg from '../Media/Svg/Svg';
import { isWhiteListedEnvironment } from '../../../utility';
import './Media.scss';
import underConstruction from './underConstruction.gif';

const Animate = React.lazy(() => import('../Media/Animate/Animate'));
// const Svg = React.lazy(() => import('../Media/Svg/Svg'));
const Images = React.lazy(() => import('../Media/Images/Images'));
const Videos = React.lazy(() => import('../Media/Videos/Videos'));
const Custom = React.lazy(() => import('../Media/Custom/Custom'));
const Table = React.lazy(() => import('../Media/Table/Table'));
const CYUQuiz = React.lazy(() => import('../Media/CYUQuiz/CYUQuiz'));
const SyntaxChecker = React.lazy(() =>
  import('../Media/SyntaxChecker/SyntaxChecker')
);
const BinaryToDecimal = React.lazy(() =>
  import('../Media/interactive-activities/BinaryToDecimal/BinaryToDecimal')
);
const DecimalToBinary = React.lazy(() =>
  import('../Media/interactive-activities/DecimalToBinary/DecimalToBinary')
);
const WildcardMasks = React.lazy(() =>
  import('../Media/interactive-activities/WildcardMasks/WildcardMasks')
);
const AndingDetermine = React.lazy(() =>
  import('../Media/interactive-activities/AndingDetermine/AndingDetermine')
);
const SubnetMask = React.lazy(() =>
  import('../Media/interactive-activities/SubnetMask/SubnetMask')
);
const IPAddressRepresentation = React.lazy(() =>
  import(
    '../Media/interactive-activities/IPAddressRepresentation/IPAddressRepresentation'
  )
);
const SwitchIt = React.lazy(() =>
  import('../Media/interactive-activities/SwitchIt/SwitchIt')
);
const BitsToBorrow = React.lazy(() =>
  import('../Media/interactive-activities/BitsToBorrow/BitsToBorrow')
);
const Matching = React.lazy(() =>
  import('../Media/interactive-activities/Matching/Matching')
);
const ListPicker = React.lazy(() =>
  import('../Media/interactive-activities/ListPicker/ListPicker')
);
const Dropdown = React.lazy(() =>
  import('../Media/interactive-activities/Dropdown/Dropdown')
);
const AsciiTranslator = React.lazy(() =>
  import('../Media/interactive-activities/ASCII-Translator/ASCII-Translator')
);

const shuffle = array => {
  for (let i = array.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
};

// Handling interactive activities separately,
// since we'll have a bunch of different types
const Ia = props => {
  // if there is no subtypes, return nothing
  if (!props.subtype) return false;
  // if the subtype is animate, we'll load up the Animate component to handle it
  if (props.subtype === 'animate')
    return <Animate id={props.id} {...props.ui} />;
  // if the subtype is react, it's an IA we're re-writing within React.
  if (props.subtype === 'react') {
    // looking for a component prop under ui
    // decimal to binary conversion
    if (props.ui.component === 'decimal-to-binary')
      return <DecimalToBinary id={props.id} {...props.ui} />;
    // binary to decimal
    if (props.ui.component === 'binary-to-decimal')
      return <BinaryToDecimal id={props.id} {...props.ui} />;
    // wildcard-mask
    if (props.ui.component === 'wildcard-mask')
      return <WildcardMasks id={props.id} {...props.ui} />;
    // anding-determine
    if (props.ui.component === 'anding-determine')
      return <AndingDetermine id={props.id} {...props.ui} />;
    // anding-determine
    if (props.ui.component === 'calculate-subnetmask')
      return <SubnetMask id={props.id} {...props.ui} />;
    // anding-determine
    if (props.ui.component === 'ipv6-address-representation')
      return <IPAddressRepresentation id={props.id} {...props.ui} />;
    // Switch It
    if (props.ui.component === 'switch-it')
      return <SwitchIt id={props.id} {...props.ui} />;
    if (props.ui.component === 'bits-to-borrow')
      return <BitsToBorrow id={props.id} {...props.ui} />;
    // Matching
    if (props.ui.component === 'matching')
      return <Matching id={props.id} {...props.ui} />;
    // List Picker
    if (props.ui.component === 'list-picker')
      return <ListPicker id={props.id} {...props.ui} />;
    // List Picker
    if (props.ui.component === 'dropdown')
      return <Dropdown id={props.id} {...props.ui} />;
    // ASCII-Translator
    if (props.ui.component === 'ascii-translator')
      return <AsciiTranslator id={props.id} {...props.ui} />;
  }
};

const MediaType = props => {
  if (props.type === 'animate') return <Animate id={props.id} {...props.ui} />;
  if (props.type === 'ia') return <Ia {...props} />;
  if (props.type === 'image')
    return props.subtype === 'svg' ? (
      <Svg uid={props.id} {...props.ui} />
    ) : (
      <Images id={props.id} subtype={props.subtype} {...props.ui} />
    );

  if (props.type === 'svg' || props.type === 'graphic')
    return <Svg uid={props.id} {...props.ui} />;
  if (props.type === 'video')
    return <Videos id={props.id} subtype={props.subtype} {...props.ui} />;

  if (props.type === 'custom') return <Custom id={props.id} {...props.ui} />;
  if (props.type === 'table') return <Table id={props.id} {...props.ui} />;
  if (props.type === 'quiz') {
    let isModQuiz =
      props.metadata &&
      props.metadata['module-quiz'] &&
      (props.metadata['module-quiz'] === true ||
        props.metadata['module-quiz'] === 'true')
        ? true
        : false;

    let questions = [...props.ui.questions];
    questions.map((question, questionIndex) => {
      if (isModQuiz) shuffle(question.options);
      return question;
    });

    return (
      <CYUQuiz
        modquiz={isModQuiz}
        questions={questions}
        id={props.id}
        {...props.ui}
      />
    );
  }
  if (props.type === 'command')
    return <CommandWindow id={props.id} {...props.ui} />;
  if (props.type === 'syntax')
    return <SyntaxChecker id={props.id} {...props.ui} />;

  return false;
};

const Media = ({ use }) => {
  let { dataUrl, courseId, dataUrlBase } = useSelector(
    state => ({
      dataUrl: state.BlockState.dataUrl,
      courseId: state.CourseState.courseId,
      dataUrlBase: state.CourseState.dataUrlBase
    }),
    shallowEqual
  );

  // we don't need to specify the .yml extension
  const mediaFilePath = use.includes('.yml') ? use : `${use}.yml`;
  // get the media file
  const [{ data: mediaFile }] = useAxios(`${dataUrl}assets/${mediaFilePath}`);
  let [parsedMedia, setParsedMedia] = useState();
  let [showDevNotes, setShowDevNotes] = useState(false);
  let [isRestrictedLogin] = useState(
    localStorage.getItem('netacad-first-look')
  );

  useEffect(() => {
    if (mediaFile) {
      // one we have the media file, parse it and set it on satate
      const parsedMedia = yaml.safeLoad(mediaFile);
      setParsedMedia(parsedMedia);
      if (parsedMedia.metadata)
        setShowDevNotes(
          parsedMedia.metadata['media-not-started'] === true ||
            parsedMedia.metadata['media-not-started'] === 'true'
        );
    }
  }, [mediaFile]);

  // loading until we have a parsed media files
  if (!parsedMedia) return <Loader context="Media" />;
  // Mbar is a special case now since it loads media, and collides with suspend is some cases
  if (parsedMedia.type === 'mbar')
    return (
      <>
        <CmsTools id={parsedMedia.id} type="medias" />
        <Mbar id={parsedMedia.id} {...parsedMedia.ui} />
      </>
    );
  return (
    <>
      {parsedMedia.transcript && (
        <div
          className="visually-hidden"
          id={`media-transcript-${parsedMedia.id}`}
        >
          <Asset id={parsedMedia.transcript} />
        </div>
      )}
      <div
        aria-describedby={`media-transcript-${parsedMedia.id}`}
        className={`media-container media media-${parsedMedia.type}${
          parsedMedia.subtype ? ` media-subtype-${parsedMedia.subtype}` : ''
        }${
          parsedMedia.ui.component
            ? ` media-component-${parsedMedia.ui.component}`
            : ''
        }${isRestrictedLogin ? ` restricted-login` : ''}`}
      >
        {parsedMedia.title && (
          <Asset className="media-title" id={parsedMedia.title} />
        )}
        {parsedMedia.type && parsedMedia.type !== 'mbar' && (
          <Suspense fallback={<Loader context="Media" />}>
            <ErrorBoundry>
              {(!parsedMedia.metadata ||
                !parsedMedia.metadata['media-not-started'] ||
                parsedMedia.metadata['media-not-started'] === 'false') && (
                <MediaType {...parsedMedia} />
              )}
              {isWhiteListedEnvironment() &&
                parsedMedia.metadata &&
                showDevNotes &&
                parsedMedia.metadata['dev-screenshot'] && (
                  <>
                    <div className="devnotes-container">
                      {parsedMedia.metadata['dev-screenshot'] && (
                        <img
                          alt={parsedMedia.metadata['dev-notes']}
                          src={`${dataUrlBase}/${courseId}/_devscreenshots/${parsedMedia.metadata['dev-screenshot']}`}
                        />
                      )}
                      {parsedMedia.metadata['dev-notes'] && (
                        <MDX>{parsedMedia.metadata['dev-notes']}</MDX>
                      )}
                    </div>
                    <div>
                      {(parsedMedia.type === 'graphic' ||
                        (parsedMedia.type === 'image' &&
                          parsedMedia.subtype === 'svg')) &&
                        parsedMedia.ui.svgs &&
                        parsedMedia.ui.svgs[0] &&
                        parsedMedia.ui.svgs[0].caption && (
                          <div className="card" style={{ minHeight: 'auto' }}>
                            <Asset id={parsedMedia.ui.svgs[0].caption} />
                          </div>
                        )}
                      {parsedMedia.type === 'image' &&
                        parsedMedia.subtype !== 'svg' &&
                        parsedMedia.ui &&
                        parsedMedia.ui.images &&
                        parsedMedia.ui.images[0].caption && (
                          <div className="card" style={{ minHeight: 'auto' }}>
                            <Asset id={parsedMedia.ui.images[0].caption} />
                          </div>
                        )}
                    </div>
                  </>
                )}
              {isWhiteListedEnvironment() &&
                parsedMedia.metadata &&
                parsedMedia.metadata['media-not-started'] &&
                parsedMedia.metadata['media-not-started'] !== 'false' &&
                showDevNotes === false && (
                  <div className="media-under-construction">
                    <img src={underConstruction} alt="under construction" />
                  </div>
                )}
            </ErrorBoundry>
            <CmsTools id={parsedMedia.id} type="medias" />
            {isWhiteListedEnvironment() &&
              parsedMedia.metadata &&
              parsedMedia.metadata['dev-notes'] !== undefined && (
                <ul className="devtools">
                  <li
                    className={`devnotes${showDevNotes ? ' active' : ''}`}
                    onClick={() => setShowDevNotes(!showDevNotes)}
                  >
                    {showDevNotes ? 'Hide' : 'Show'} DevNotes
                  </li>
                </ul>
              )}
          </Suspense>
        )}
      </div>
    </>
  );
};

export default React.memo(Media);
